import { FC, useMemo, useState } from 'react';

import {
  getDashboardCustomerAnalytics,
  getDashboardProfitAnalysisDetail,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  CustomerAnalyticsItemType,
  CustomerAnalyticsResponse,
  CustomerStatSummary,
  GiftItemProfitRow,
  ItemPurchaseRow,
  UnitPriceDistributionBucket,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import {
  CenterLoading,
  ErrorBox,
  TD_CLASS,
  TH_CLASS,
} from '../../components/DashboardShared';
import { useCachedResource } from './analysisCache';
import { PriceBandImpactSection } from './PriceBandImpactSection';
import {
  fmt,
  getDatePreset,
  getLocalToday,
  getNiceScale,
  toDateStr,
} from './analysisUtils';

// 서버 share는 0–1 (소수 4자리)
const fmtShare = (share: number): string => `${(share * 100).toFixed(1)}%`;

// 런타임엔 axios 인터셉터가 ISO 문자열을 Date로 바꿔놓는다 — new Date()로 흡수
const fmtDate = (v?: string | null): string =>
  v ? toDateStr(new Date(v)) : '-';

// 산점도 눈금용 축약 (15000 → 1.5만)
const fmtWonTick = (v: number): string =>
  v >= 10000 ? `${v / 10000}만` : fmt(v);

const TYPE_LABEL: Record<ItemPurchaseRow['type'], string> = {
  Coupon: '쿠폰',
  Delivery: '배송',
};
const TYPE_HEX: Record<ItemPurchaseRow['type'], string> = {
  Coupon: '#6366f1',
  Delivery: '#10b981',
};

const ITEM_TYPE_FILTERS: {
  key: 'all' | CustomerAnalyticsItemType;
  label: string;
}[] = [
  { key: 'all', label: '전체' },
  { key: 'Coupon', label: '쿠폰형' },
  { key: 'Delivery', label: '배송형' },
];

interface Props {
  from?: string;
  to?: string;
}

/**
 * 고객 분석 탭 — 기간 내 주문을 구매 user 단위로 집계 (customer-analytics API).
 * "한 명이 보통 얼마치·몇 개를·얼마짜리로 사는가". 산법은 요약탭과 동일 집합 공유.
 * 스펙: docs/gift-order-customer-analytics-spec.md
 */
export const CustomerTab: FC<Props> = function CustomerTab({ from, to }) {
  // 기간 필터가 비어있으면(직접선택 미적용 등) 최근 3개월
  const fallback = useMemo(() => getDatePreset('3m'), []);
  const effFrom = from ?? fallback.from ?? toDateStr(getLocalToday());
  const effTo = to ?? toDateStr(getLocalToday());

  const [top, setTop] = useState(20);
  // 상품유형 필터(v2) — 혼합 주문의 결제액은 유형별로 쪼갤 수 없어 서버 refetch로만 정확하다
  const [itemType, setItemType] = useState<'all' | CustomerAnalyticsItemType>(
    'all',
  );

  //* 조건이 같으면 다시 안 부른다 — 토글을 되돌리거나 탭을 오갈 때마다 같은 집계가
  //* 서버에서 새로 돌던 걸 막는다 (구매자 단위 집계라 가볍지 않다)
  const {
    data,
    loading,
    error,
    notReady, // 구서버 404
    refetch: fetchData,
  } = useCachedResource<CustomerAnalyticsResponse>(
    `customerAnalytics|${effFrom}|${effTo}|${top}|${itemType}`,
    () => {
      // 결제일(purchasedAt) 축, KST 하루 경계로 변환해 전달
      const fromIso = new Date(`${effFrom}T00:00:00+09:00`).toISOString();
      const toIso = new Date(`${effTo}T23:59:59.999+09:00`).toISOString();
      return getDashboardCustomerAnalytics(
        fromIso,
        toIso,
        top,
        itemType === 'all' ? undefined : itemType,
      );
    },
    { errorMessage: '고객 분석 데이터를 불러올 수 없습니다.' },
  );

  // 임팩트 분석용 밴드 마진 데이터 — 유형 토글과 무관하니 기간당 1회만 가져와
  // 클라이언트에서 type으로 거른다. 실패해도 임팩트 섹션이 매출 비중 기준으로 동작하므로 조용히 넘어간다.
  const { data: profitRows } = useCachedResource<GiftItemProfitRow[] | null>(
    `customerProfitDetail|${effFrom}|${effTo}`,
    async () => {
      const fromIso = new Date(`${effFrom}T00:00:00+09:00`).toISOString();
      const toIso = new Date(`${effTo}T23:59:59.999+09:00`).toISOString();
      try {
        return (await getDashboardProfitAnalysisDetail(fromIso, toIso)).giftItems;
      } catch {
        //* 부가 데이터 — 실패해도 임팩트 섹션이 매출 비중 기준으로 동작한다
        return null;
      }
    },
  );

  if (loading) return <CenterLoading />;
  if (notReady) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
        고객 분석 API가 아직 이 서버에 배포되지 않았습니다. 배포 후 다시
        열어주세요.
      </div>
    );
  }
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return null;

  const { buyers, byItem, topBuyers } = data;

  // 필터 결과가 비어도 되돌아갈 수 있게 토글은 빈 상태에서도 그린다
  const typeToggle = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex gap-1">
        {ITEM_TYPE_FILTERS.map((t) => (
          <button
            key={t.key}
            onClick={() => setItemType(t.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              itemType === t.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {itemType !== 'all' && (
        <p className="text-xs text-amber-600">
          유형 필터 중에는 구매액이 결제액이 아닌 해당 유형
          상품금액(순수량×동결단가) 기준 — 전체 보기와 합산 비교 불가
        </p>
      )}
    </div>
  );

  if (buyers.buyerCount === 0) {
    return (
      <div className="space-y-6">
        {typeToggle}
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <p className="text-gray-500">
            해당 기간에{' '}
            {itemType !== 'all' && `${TYPE_LABEL[itemType]}형 상품 `}구매자가
            없습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {typeToggle}

      {/* KPI 카드 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">구매자 수</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(buyers.buyerCount)}명
          </p>
          <p className="mt-1 text-xs text-gray-400">
            주문 {fmt(buyers.orderCount)}건
            {buyers.canceledOrderCount > 0 &&
              ` · 전체취소 ${fmt(buyers.canceledOrderCount)}건`}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">1인당 평균 구매액</p>
          <p className="mt-1 text-2xl font-bold text-green-600">
            {fmt(buyers.spendPerBuyer.avg)}원
          </p>
          <p className="mt-1 text-xs text-gray-400">
            중앙값 {fmt(buyers.spendPerBuyer.median)}원
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">1인당 평균 수량</p>
          <p className="mt-1 text-2xl font-bold text-indigo-600">
            {buyers.quantityPerBuyer.avg.toFixed(1)}개
          </p>
          <p className="mt-1 text-xs text-gray-400">
            중앙값 {buyers.quantityPerBuyer.median.toFixed(1)}개 · 총{' '}
            {fmt(buyers.totalNetQuantity)}개
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">평균 단가 (얼마짜리 선물)</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {fmt(buyers.unitPricePerBuyer.avg)}원
          </p>
          <p className="mt-1 text-xs text-gray-400">
            중앙값 {fmt(buyers.unitPricePerBuyer.median)}원
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-xs text-gray-500">재구매율</p>
          <p className="mt-1 text-2xl font-bold text-orange-600">
            {fmtShare(buyers.repeatBuyerRate)}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            재구매 {fmt(buyers.repeatBuyerCount)}명 · 인당 주문{' '}
            {buyers.avgOrdersPerBuyer.toFixed(1)}건
          </p>
        </div>
      </div>

      {/* 분포 히스토그램 3종 */}
      <div className="grid gap-6 lg:grid-cols-3">
        <DistCard
          title="구매액 분포"
          sub="1인당 총 구매액 구간별 구매자 수"
          bars={buyers.spendDistribution.map((b) => ({
            label: b.label,
            value: b.buyerCount,
            hint: `${b.label}: ${fmt(b.buyerCount)}명 (${fmtShare(b.share)})`,
          }))}
        />
        <DistCard
          title="수량 분포"
          sub="1인당 구매 수량 구간별 구매자 수"
          bars={buyers.quantityDistribution.map((b) => ({
            label: b.label,
            value: b.buyerCount,
            hint: `${b.label}: ${fmt(b.buyerCount)}명 (${fmtShare(b.share)})`,
          }))}
        />
        <UnitPriceDualCard buckets={buyers.unitPriceDistribution} />
      </div>

      {/* 어느 가격대에 주력할지 — 매출 비중 × 마진 개선 여지 */}
      <PriceBandImpactSection
        analytics={data}
        itemType={itemType}
        profitRows={
          profitRows === null
            ? null
            : profitRows.filter(
                (r) => itemType === 'all' || r.type === itemType,
              )
        }
      />

      {/* 백분위 요약 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            구매자 분포 백분위
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={TH_CLASS}>지표</th>
                <th className={`${TH_CLASS} text-right`}>최소</th>
                <th className={`${TH_CLASS} text-right`}>p25</th>
                <th className={`${TH_CLASS} text-right`}>중앙값</th>
                <th className={`${TH_CLASS} text-right`}>평균</th>
                <th className={`${TH_CLASS} text-right`}>p75</th>
                <th className={`${TH_CLASS} text-right`}>p90</th>
                <th className={`${TH_CLASS} text-right`}>최대</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <PercentileRow
                label="1인당 구매액"
                stat={buyers.spendPerBuyer}
                format={(v) => `${fmt(Math.round(v))}원`}
              />
              <PercentileRow
                label="1인당 수량"
                stat={buyers.quantityPerBuyer}
                format={(v) => `${v.toFixed(1)}개`}
              />
              <PercentileRow
                label="구매자별 평균단가"
                stat={buyers.unitPricePerBuyer}
                format={(v) => `${fmt(Math.round(v))}원`}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* 상품 산점도: 단가 × 1인당 수량 */}
      {byItem.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-sm font-semibold text-gray-800">
            상품별 단가 × 1인당 수량
          </h3>
          <p className="mt-1 mb-4 text-xs text-gray-400">
            왼쪽 위일수록 "싼 상품을 많이", 오른쪽 아래일수록 "비싼 상품을
            적게" — 점 크기는 구매자 수, 색은 상품 유형
          </p>
          <ItemScatter items={byItem} />
          <div className="mt-2 flex justify-end gap-4">
            {(Object.keys(TYPE_LABEL) as ItemPurchaseRow['type'][])
              .filter((t) => byItem.some((i) => i.type === t))
              .map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-1.5 text-xs text-gray-500"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: TYPE_HEX[t] }}
                  />
                  {TYPE_LABEL[t]}형
                </span>
              ))}
          </div>
        </div>
      )}

      {/* 상품별 구매행태 테이블 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            상품별 1인당 구매행태 ({fmt(byItem.length)}종, 순매출순)
          </h3>
        </div>
        <div className="max-h-[420px] overflow-x-auto overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className={TH_CLASS}>상품명</th>
                <th className={TH_CLASS}>유형</th>
                <th className={`${TH_CLASS} text-right`}>평균단가</th>
                <th className={`${TH_CLASS} text-right`}>구매자</th>
                <th className={`${TH_CLASS} text-right`}>순수량</th>
                <th className={`${TH_CLASS} text-right`}>순매출</th>
                <th className={`${TH_CLASS} text-right`}>1인당 수량</th>
                <th className={`${TH_CLASS} text-right`}>1인당 구매액</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {byItem.map((item) => (
                <tr key={item.giftItemId} className="hover:bg-gray-50">
                  <td className={`${TD_CLASS} max-w-[320px]`}>
                    <span className="block truncate" title={item.name}>
                      {item.name}
                    </span>
                  </td>
                  <td className={TD_CLASS}>
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      style={{ backgroundColor: TYPE_HEX[item.type] }}
                    >
                      {TYPE_LABEL[item.type]}
                    </span>
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(item.avgUnitPrice)}원
                    {item.avgUnitPrice !== item.priceRetail && (
                      <span className="block text-xs text-gray-400">
                        카탈로그 {fmt(item.priceRetail)}원
                      </span>
                    )}
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(item.buyerCount)}명
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(item.netQuantity)}개
                    {item.cancelQuantity > 0 && (
                      <span className="block text-xs text-gray-400">
                        취소 {fmt(item.cancelQuantity)}
                      </span>
                    )}
                  </td>
                  <td className={`${TD_CLASS} text-right font-medium`}>
                    {fmt(item.netAmount)}원
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {item.avgQuantityPerBuyer.toFixed(1)}개
                    <span className="block text-xs text-gray-400">
                      중앙 {item.medianQuantityPerBuyer.toFixed(1)} · 최대{' '}
                      {fmt(item.maxQuantityPerBuyer)}
                    </span>
                  </td>
                  <td className={`${TD_CLASS} text-right text-gray-500`}>
                    {fmt(item.avgSpendPerBuyer)}원
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 상위 구매자 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">
            상위 구매자 (순구매액순)
          </h3>
          <div className="flex gap-1">
            {[20, 50, 100].map((n) => (
              <button
                key={n}
                onClick={() => setTop(n)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  top === n
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {n}명
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[480px] overflow-x-auto overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className={TH_CLASS}>#</th>
                <th className={TH_CLASS}>구매자</th>
                <th className={TH_CLASS}>연락처</th>
                <th className={`${TH_CLASS} text-right`}>주문수</th>
                <th className={`${TH_CLASS} text-right`}>순구매액</th>
                <th className={`${TH_CLASS} text-right`}>순수량</th>
                <th className={`${TH_CLASS} text-right`}>평균단가</th>
                <th className={`${TH_CLASS} text-right`}>상품종류</th>
                <th className={`${TH_CLASS} text-right`}>첫 구매</th>
                <th className={`${TH_CLASS} text-right`}>최근 구매</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topBuyers.map((b, i) => (
                <tr key={b.userId} className="hover:bg-gray-50">
                  <td className={`${TD_CLASS} text-gray-400`}>{i + 1}</td>
                  <td className={`${TD_CLASS} font-medium`}>{b.name ?? '-'}</td>
                  <td className={TD_CLASS}>{b.phoneNumber ?? '-'}</td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(b.orderCount)}건
                  </td>
                  <td className={`${TD_CLASS} text-right font-medium`}>
                    {fmt(b.netSpend)}원
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(b.netQuantity)}개
                  </td>
                  <td className={`${TD_CLASS} text-right text-gray-500`}>
                    {fmt(b.avgUnitPrice)}원
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    {fmt(b.itemKindCount)}종
                  </td>
                  <td className={`${TD_CLASS} text-right text-gray-500`}>
                    {fmtDate(b.firstPurchasedAt)}
                  </td>
                  <td className={`${TD_CLASS} text-right text-gray-500`}>
                    {fmtDate(b.lastPurchasedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

/** 버킷 분포 수직 막대 카드. 빈 버킷도 서버가 전부 내려주므로 그대로 그린다. */
const DistCard: FC<{
  title: string;
  sub: string;
  bars: { label: string; value: number; hint: string }[];
}> = ({ title, sub, bars }) => {
  const scale = getNiceScale(Math.max(...bars.map((b) => b.value), 1));
  return (
    <div className="flex flex-col rounded-lg bg-white p-6 shadow">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <p className="mb-4 text-xs text-gray-400">{sub}</p>
      <div className="flex h-40 items-end justify-around gap-1">
        {bars.map((bar) => {
          const pct =
            scale.niceMax > 0 ? (bar.value / scale.niceMax) * 100 : 0;
          return (
            <div
              key={bar.label}
              className="group relative flex h-full flex-1 flex-col items-center justify-end"
            >
              <div className="pointer-events-none absolute -top-1 z-10 hidden -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
                {bar.hint}
              </div>
              <span className="mb-1 text-[11px] font-bold text-gray-700">
                {bar.value > 0 ? fmt(bar.value) : ''}
              </span>
              <div
                className="w-full max-w-[40px] rounded-t bg-indigo-500 transition-all duration-500"
                style={{ height: `${pct}%`, minHeight: bar.value > 0 ? 4 : 0 }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-around gap-1">
        {bars.map((bar) => (
          <span
            key={bar.label}
            className="flex-1 text-center text-[10px] leading-tight text-gray-500"
          >
            {bar.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * 가격대별 수량 비중(share) × 매출 비중(amountShare) 이중 막대.
 * 둘 다 0–1 비중이라 같은 % 축에 그린다 — 두 막대가 어긋나는 밴드가 개선 포인트다:
 * 수량↑·매출↓는 upsell(옵션 추가금) 설계, 수량↓·매출↑는 도매가 협상이 유효 (스펙 문서 참고).
 */
const UnitPriceDualCard: FC<{ buckets: UnitPriceDistributionBucket[] }> = ({
  buckets,
}) => {
  const maxPct = Math.max(
    ...buckets.map((b) => Math.max(b.share, b.amountShare) * 100),
    1,
  );
  const scale = getNiceScale(maxPct);
  const barHeight = (share: number) =>
    scale.niceMax > 0 ? ((share * 100) / scale.niceMax) * 100 : 0;

  return (
    <div className="flex flex-col rounded-lg bg-white p-6 shadow">
      <h3 className="text-sm font-semibold text-gray-800">
        가격대별 수량·매출 비중
      </h3>
      <p className="mb-4 text-xs text-gray-400">
        많이 팔리는 가격대와 돈이 되는 가격대의 괴리를 본다
      </p>
      <div className="flex h-40 items-end justify-around gap-1">
        {buckets.map((b) => (
          <div
            key={b.label}
            className="group relative flex h-full flex-1 items-end justify-center gap-0.5"
          >
            <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded bg-gray-800 px-2 py-1 text-[10px] leading-tight whitespace-nowrap text-white shadow group-hover:block">
              {b.label}: 수량 {fmt(b.quantity)}개 ({fmtShare(b.share)}) · 매출{' '}
              {fmt(b.netAmount)}원 ({fmtShare(b.amountShare)}) · 구매자{' '}
              {fmt(b.buyerCount)}명
            </div>
            <div
              className="w-full max-w-[18px] rounded-t bg-indigo-400 transition-all duration-500"
              style={{
                height: `${barHeight(b.share)}%`,
                minHeight: b.quantity > 0 ? 4 : 0,
              }}
            />
            <div
              className="w-full max-w-[18px] rounded-t bg-emerald-500 transition-all duration-500"
              style={{
                height: `${barHeight(b.amountShare)}%`,
                minHeight: b.netAmount > 0 ? 4 : 0,
              }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-around gap-1">
        {buckets.map((b) => (
          <span
            key={b.label}
            className="flex-1 text-center text-[10px] leading-tight text-gray-500"
          >
            {b.label}
          </span>
        ))}
      </div>
      <div className="mt-3 flex justify-center gap-4">
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-400" />
          수량 비중
        </span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
          매출 비중
        </span>
      </div>
    </div>
  );
};

const PercentileRow: FC<{
  label: string;
  stat: CustomerStatSummary;
  format: (v: number) => string;
}> = ({ label, stat, format }) => (
  <tr className="hover:bg-gray-50">
    <td className={`${TD_CLASS} font-medium`}>{label}</td>
    <td className={`${TD_CLASS} text-right text-gray-500`}>
      {format(stat.min)}
    </td>
    <td className={`${TD_CLASS} text-right`}>{format(stat.p25)}</td>
    <td className={`${TD_CLASS} text-right font-bold`}>
      {format(stat.median)}
    </td>
    <td className={`${TD_CLASS} text-right`}>{format(stat.avg)}</td>
    <td className={`${TD_CLASS} text-right`}>{format(stat.p75)}</td>
    <td className={`${TD_CLASS} text-right`}>{format(stat.p90)}</td>
    <td className={`${TD_CLASS} text-right text-gray-500`}>
      {format(stat.max)}
    </td>
  </tr>
);

/** 단가 × 1인당 수량 산점도. 점 크기 = 구매자 수. */
const ItemScatter: FC<{ items: ItemPurchaseRow[] }> = ({ items }) => {
  const W = 560;
  const H = 300;
  const ML = 48; // Y축 라벨 여백
  const MB = 28; // X축 라벨 여백
  const MT = 12;
  const MR = 16;
  const plotW = W - ML - MR;
  const plotH = H - MT - MB;

  const xScale = getNiceScale(Math.max(...items.map((i) => i.avgUnitPrice), 1));
  const yScale = getNiceScale(
    Math.max(...items.map((i) => i.avgQuantityPerBuyer), 1),
  );
  const maxBuyer = Math.max(...items.map((i) => i.buyerCount), 1);

  const px = (v: number) => ML + (v / xScale.niceMax) * plotW;
  const py = (v: number) => MT + plotH - (v / yScale.niceMax) * plotH;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mx-auto w-full max-w-3xl min-w-[480px]"
      >
        {/* 가이드 선 + 눈금 */}
        {yScale.ticks.map((t) => (
          <g key={`y${t}`}>
            <line
              x1={ML}
              x2={W - MR}
              y1={py(t)}
              y2={py(t)}
              stroke="#f3f4f6"
              strokeWidth={1}
            />
            <text
              x={ML - 6}
              y={py(t)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={10}
              fill="#9ca3af"
            >
              {t}개
            </text>
          </g>
        ))}
        {xScale.ticks.map((t) => (
          <g key={`x${t}`}>
            <line
              x1={px(t)}
              x2={px(t)}
              y1={MT}
              y2={MT + plotH}
              stroke="#f3f4f6"
              strokeWidth={1}
            />
            <text
              x={px(t)}
              y={H - MB + 14}
              textAnchor="middle"
              fontSize={10}
              fill="#9ca3af"
            >
              {fmtWonTick(t)}
            </text>
          </g>
        ))}
        {/* 축 */}
        <line
          x1={ML}
          x2={W - MR}
          y1={MT + plotH}
          y2={MT + plotH}
          stroke="#d1d5db"
          strokeWidth={1}
        />
        <line
          x1={ML}
          x2={ML}
          y1={MT}
          y2={MT + plotH}
          stroke="#d1d5db"
          strokeWidth={1}
        />
        {/* 점 — 작은 점이 위에 오도록 구매자 수 내림차순으로 그린다 */}
        {[...items]
          .sort((a, b) => b.buyerCount - a.buyerCount)
          .map((item) => (
            <circle
              key={item.giftItemId}
              cx={px(item.avgUnitPrice)}
              cy={py(item.avgQuantityPerBuyer)}
              r={4 + 10 * Math.sqrt(item.buyerCount / maxBuyer)}
              fill={TYPE_HEX[item.type]}
              fillOpacity={0.55}
              stroke={TYPE_HEX[item.type]}
              strokeWidth={1}
            >
              <title>
                {`${item.name}\n단가 ${fmt(item.avgUnitPrice)}원 · 1인당 ${item.avgQuantityPerBuyer.toFixed(1)}개 · 구매자 ${fmt(item.buyerCount)}명`}
              </title>
            </circle>
          ))}
      </svg>
    </div>
  );
};
